/* eslint-disable react/no-unknown-property */
import { useMemo, useRef } from 'react';
import { Color } from 'three';
import { OrbitControls } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import HandStuffedContainer from './HandStuffedContainer';
import ViewportControls from './ViewportControls';
import CameraSync from './CameraSync';
import { applyCameraTransform } from './cameraTransform';

const DIVISOR = 2;

// Shown on the form (pre-run) view. Reuses the exact same shell-rendering
// component, Canvas setup, grid, lighting, camera formula, and pan/zoom pad
// as the real results viewer (HandStuffedModel) — not a separate invented
// style — by fabricating the same {entities, packedContainers} shape that
// component expects, with stacks: [] so it draws just the container shell
// and label, no packed items (because none have been simulated yet).
export default function EmptyArenaPreview({ containers }) {
  const controlsRef = useRef(null);

  const { entities, packedContainers } = useMemo(() => {
    const entityContainers = {};
    // Skip containers still mid-edit with any dimension at 0/blank — a
    // custom container being typed into (or a standard one manually zeroed
    // out) would otherwise render as a degenerate flat plane instead of
    // just not showing up yet.
    const complete = containers.filter((c) => Number(c.length) > 0 && Number(c.width) > 0 && Number(c.height) > 0);
    const packed = complete.map((c, i) => {
      const id = c.name || `Container ${i + 1}`;
      entityContainers[id] = { length: Number(c.length) || 0, width: Number(c.width) || 0, height: Number(c.height) || 0 };
      return { id, quantity: 1, stacks: [] };
    });
    return { entities: { containers: entityContainers, items: {} }, packedContainers: packed };
  }, [containers]);

  const totalWidth = useMemo(() => {
    const cum = packedContainers.reduce((acc, c, i) => {
      const w = entities.containers[c.id]?.width || 0;
      return acc + w + (i === packedContainers.length - 1 ? 0 : DIVISOR);
    }, 0);
    return cum / DIVISOR;
  }, [packedContainers, entities]);

  const maxContainerHeight = Math.max(0, ...packedContainers.map((c) => (entities.containers[c.id]?.height || 0) / DIVISOR));
  const maxContainerLength = Math.max(0, ...packedContainers.map((c) => (entities.containers[c.id]?.length || 0) / DIVISOR));

  const minContainerWidth = useMemo(() => {
    const vals = packedContainers.map((c) => (entities.containers[c.id]?.width || 0) / DIVISOR);
    return vals.length ? Math.min(...vals) : 0;
  }, [packedContainers, entities]);

  let currentZ = -totalWidth * 0.9;
  const getZValue = (idx) => {
    const c = packedContainers[idx];
    if (!c) return 0;
    const w = (entities.containers[c.id]?.width || 0) / DIVISOR;
    const z = currentZ;
    currentZ += w + minContainerWidth;
    return z;
  };

  const cameraPosition = useMemo(() => {
    if (!packedContainers.length) return [0, 20, 50];
    const distance = Math.max(totalWidth, maxContainerLength, maxContainerHeight) * 1.5;
    return applyCameraTransform(distance, { zoom: 0.70, rotationDeg: 10, tiltDeg: 5 });
  }, [packedContainers, totalWidth, maxContainerLength, maxContainerHeight]);

  return (
    <div className="relative w-full" style={{ height: 400 }}>
      <div className="absolute inset-0 rounded-xl overflow-hidden">
        <Canvas
          style={{ width: '100%', height: '100%' }}
          scene={{ background: new Color(0x000000) }}
          camera={{ position: cameraPosition, fov: 50, near: 1, far: 1000 }}
        >
          <gridHelper args={[1000, 120, 0xffffff, 0xffffff]} material-opacity={0.25} material-transparent material-linewidth={1.0} position={[0, 0, 0]} />
          <ambientLight intensity={0.7} color={0xffffff} />
          <directionalLight position={[50, 50, 50]} intensity={0.8} color={0xffffff} castShadow />
          <directionalLight position={[0, -50, 0]} intensity={0.6} color={0xffffff} />
          <OrbitControls ref={controlsRef} makeDefault />
          <CameraSync position={cameraPosition} controlsRef={controlsRef} />
          {packedContainers.map((c, i) => (
            <HandStuffedContainer key={`empty-${i}`} container={c} entities={entities} zValue={getZValue(i)} animationProgress={0} edgeColor="#0d7ff2" />
          ))}
        </Canvas>
      </div>

      <ViewportControls controlsRef={controlsRef} />

      {packedContainers.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <p className="pointer-events-auto rounded-lg border border-white/10 bg-black/50 px-4 py-2 text-sm font-semibold text-slate-300 backdrop-blur-sm">
            Pick a{" "}
            <a href="#beyond-cbm-arena" className="text-primary underline decoration-primary/40 underline-offset-2 hover:text-white hover:decoration-white/60 transition">
              container
            </a>{" "}
            below — it'll appear here, ready to load.
          </p>
        </div>
      )}
    </div>
  );
}
